/**
 * Die Liedkarte — der EINE Besitzer von Text und Ja. (07.10.2026)
 *
 * Martin, 07.10. 11:14: *„Ich habe gedacht, du zeigst es mir vorher.“* Der
 * Liedtext stand bis hierher nirgends: Die Vorschlagskarte sagte nur „Noch
 * nicht erzeugt“, und das Ja holte Vespera mit einer eigenen Rückfrage-Karte,
 * deren Frage sie selbst schrieb. Zwei Besitzer — ihr Satz „zeige ich dir
 * vorher“ und eine Karte mit „Ja, erzeugen“ widersprachen sich im selben Zug.
 *
 * Jetzt trägt diese Karte alles, wozu Martin Ja sagt, und EIN Ja (11.10.2026, Ticket 3077: bei
 * einem Vorschlag mit Probestück zwei Wege zu diesem einen Ja — sie sperren sich gegenseitig):
 *   - Vorschlag: Titel, Gesang und Sprache, Länge, Qualität, Stil und der
 *     Text IM WORTLAUT — dieselben Zeichen, die an den Dienst gehen
 *     (tools/generate_music.ts legt den Vorschlag ab). Hinzugedichtetes ist
 *     markiert. Knopf: „Ja, Probestück erzeugen“ und daneben „Ja, ganzes
 *     Lied direkt erzeugen“ (ohne Probestück, zählt ins Kontingent). Steht
 *     „Musik ohne Probestück“ an, gibt es nur „Ja, ganzes Lied erzeugen“.
 *   - Probestück: der Player, was die Prüfung fand, Knopf „Ja, ganzes Lied
 *     erzeugen“. Der Hinweis sagt ehrlich: nur eine grobe Stilprobe (Ticket 3075).
 *   - Lied: der Player, die Prüfung, der gesungene Text eingeklappt.
 *   - Anbieter (Ticket 3071): der Vorschlag nennt den Dienst und lässt ihn wählen, wo der Tarif zwei Dienste erlaubt (Lyria, ElevenLabs);
 *     der Knopf trägt die Wahl mit („· anbieter elevenlabs“ hinter der Kennung, der SERVER liest sie). Probestück und Lied nennen,
 *     wer WIRKLICH komponiert hat. Suno ist nicht angebunden (Ticket 3003) und steht nirgends zur Wahl.
 *
 * ⚠️ Der Text wird NICHT umgeschrieben: „[Verse]“ bleibt „[Verse]“, nur
 *    leiser gesetzt. `textContent` des Blatts ist Zeichen für Zeichen der
 *    Text des Vorschlags (tests/frontend/lied-karte.test.ts).
 * ⚠️ Der Knopf schickt sein Ja mit Bezug (Titel und vorschlag_id), wie die
 *    Rückfrage-Karte — der Bezug geht an die KI, angezeigt bleibt das Wort.
 */
import { baueKarte, kartenTitel } from "./karte.js";
import { ANBIETER_ANZEIGE, istFortschrittAnbieter } from "../../shared/lied-fortschritt.js";
import { anbieterMarke, anbieterWahlAus, type LiedAnbieter } from "../../shared/lied-anbieter.js";
import { baueTippHinweis, type Abspieler } from "./abspielen.js";
import { icon } from "./icons.js";
import { auspacken } from "./schritte.js";

interface Vorschlag {
  title?: string;
  prompt?: string;
  length_sec?: number;
  gesang?: boolean;
  instrumental?: boolean;
  sprache_name?: string;
  sprache?: string;
  lyrics?: string;
  hinzugedichtet?: unknown;
  qualitaet?: string;
}

/** Radio-Gruppen brauchen je Karte einen eigenen Namen. */
let wahlZaehler = 0;

interface Pruefung {
  gehoert?: boolean;
  /** Nur wahr, wenn Gesang, Sprache und Text wirklich gehört und bestätigt sind (server/musik-pruefung.ts). */
  passt?: boolean;
  gesang?: string;
  sprache?: string;
  dauer_sek?: number | null;
  ungeprueft?: string;
}

const text = (v: unknown): string => (typeof v === "string" ? v : "");

function mmss(sek: number): string {
  if (!Number.isFinite(sek)) return "";
  const s = Math.max(0, Math.round(sek));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, inhalt?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (inhalt !== undefined) e.textContent = inhalt;
  return e;
}

/** Bereiche [von, bis) im Text, an denen eine der Stellen steht — zusammengeführt. */
export function markenBereiche(t: string, stellen: readonly string[]): Array<[number, number]> {
  const roh: Array<[number, number]> = [];
  for (const s of stellen) {
    if (!s) continue;
    let i = t.indexOf(s);
    while (i >= 0) {
      roh.push([i, i + s.length]);
      i = t.indexOf(s, i + s.length);
    }
  }
  roh.sort((a, b) => a[0] - b[0]);
  const raus: Array<[number, number]> = [];
  for (const b of roh) {
    const letzter = raus[raus.length - 1];
    if (letzter && b[0] <= letzter[1]) letzter[1] = Math.max(letzter[1], b[1]);
    else raus.push([b[0], b[1]]);
  }
  return raus;
}

const TEIL_ZEILE = /^\s*\[[^\]\n]{1,40}\]\s*$/;

/**
 * Das Liedblatt: der Text Zeichen für Zeichen, Abschnittszeilen („[Chorus]“)
 * leiser, hinzugedichtete Stellen markiert.
 */
function baueLiedBlatt(t: string, hinzu: readonly string[], sprache?: string): HTMLElement {
  const blatt = el("div", "lied-blatt");
  if (sprache) blatt.lang = sprache;
  const marken = markenBereiche(t, hinzu);
  let pos = 0;
  for (const zeile of t.split(/(?<=\n)/)) {
    const von = pos;
    const bis = pos + zeile.length;
    pos = bis;
    if (TEIL_ZEILE.test(zeile.replace(/\n$/, ""))) {
      blatt.appendChild(el("span", "lied-teil", zeile));
      continue;
    }
    let i = von;
    for (const [mv, mb] of marken) {
      if (mb <= i || mv >= bis) continue;
      const a = Math.max(mv, i);
      const b = Math.min(mb, bis);
      if (a > i) blatt.appendChild(document.createTextNode(t.slice(i, a)));
      const m = el("mark", "lied-hinzu", t.slice(a, b));
      m.title = "Hinzugedichtet — nicht aus deinen Worten";
      blatt.appendChild(m);
      i = b;
    }
    if (i < bis) blatt.appendChild(document.createTextNode(t.slice(i, bis)));
  }
  return blatt;
}

/** Den Ja-Knopf merken — nach dem Neuladen schickt dieselbe Karte nicht ein zweites Ja. */
const JA_SCHLUESSEL = "voice-v8.lied-ja.";
function schonJa(id: string): boolean {
  try {
    return localStorage.getItem(JA_SCHLUESSEL + id) === "1";
  } catch {
    return false;
  }
}
function merkeJa(id: string): void {
  try {
    localStorage.setItem(JA_SCHLUESSEL + id, "1");
  } catch {
    /* ohne Gerätespeicher gilt es bis zum Neuladen */
  }
}
function vergissJa(id: string): void {
  try {
    localStorage.removeItem(JA_SCHLUESSEL + id);
  } catch {
    /* ohne Gerätespeicher nichts zu vergessen */
  }
}

/** Das Ereignis, mit dem ein Werkzeug-Ergebnis seiner Karte sagt, was aus ihrem Ja wurde. */
const LIED_STAND = "vespera:lied-stand";

interface LiedStand {
  /** `<vorschlag_id>.<probe|lied>` — derselbe Schlüssel wie der Knopf. */
  schluessel: string;
  ok: boolean;
  satz: string;
}

/**
 * Ein Ergebnis von generate_music kam an (live oder aus dem Verlauf): die Karte, deren Ja dazu gehört, erfährt es.
 * (07.10.2026, dritte Prüfrunde: die Karte sagte „Bestätigt — das Probestück entsteht.“ schon beim Klick, auch wenn
 * der Server danach ablehnte.) Erst diese Zusage oder Absage des Servers ändert die Zeile unter dem Knopf.
 */
export function meldeLiedStand(result: unknown, isError: boolean): void {
  const r = auspacken(result);
  const id = text(r.vorschlag_id);
  const stufe = r.stufe === "probe" ? "probe" : r.stufe === "lied" ? "lied" : "";
  if (!id || !stufe || r.status === "vorschlag") return;
  const ok = !isError && r.ok === true && r.erzeugt === true;
  const fehler = text(r.error).replace(/^NICHT erzeugt:\s*/, "");
  // 21.10.2026: das Ja-Tor hielt an und will ein neues Ja — die Karte wird wieder tippbar (Absage, siehe `jaKnoepfe`).
  const satz = ok
    ? stufe === "probe"
      ? "Bestätigt — das Probestück ist da (unten)."
      : "Bestätigt — das Lied ist da (unten)."
    : r.bestaetigung_noetig === true
      ? "Noch nicht erzeugt — bitte hier noch einmal auf Ja tippen."
      : `Nicht erzeugt: ${fehler || "der Dienst hat abgelehnt."}`;
  document.dispatchEvent(new CustomEvent<LiedStand>(LIED_STAND, { detail: { schluessel: `${id}.${stufe}`, ok, satz } }));
}

/**
 * Ein Weg zum Ja: das Wort des Knopfs, der Bezug für die KI und zwei Schlüssel:
 *   - `schluessel`: wer dieser Knopf ist — `<vorschlag_id>.<probe|direkt|lied>`; darunter merkt sich das Gerät sein Ja.
 *   - `antwort`: auf welche Antwort des Servers er hört — `<vorschlag_id>.<probe|lied>` (`meldeLiedStand`).
 * Der direkte Weg der Vorschlagskarte heißt `.direkt`, hört aber auf `.lied` — so teilt er sich mit dem Knopf der Probestück-Karte
 * (`.lied`) weder das Gemerkte noch den Rahmen „gewählt“ (Prüfbefund Opus 11.10.2026).
 */
interface JaOption {
  wort: string;
  /** Der Bezug für die KI — bei einer Wahl auf der Karte erst beim Druck gebaut (die Wahl kann sich bis dahin ändern). */
  bezug: string | (() => string);
  schluessel: string;
  antwort: string;
}

/**
 * Welche Karte zuletzt auf eine Antwort gewartet hat (`antwort`-Schlüssel → Hülle der Karte). Antwortet der Server auf einen Druck
 * der Probestück-Karte, betrifft das die Vorschlagskarte desselben Vorschlags nicht — und umgekehrt. Hat NIEMAND gedrückt (der
 * Verlauf wird neu gebaut), hören alle Karten dieses Vorschlags: die Antwort stimmt für jede. Eine Karte, die nicht mehr im Seitenbaum
 * steht, hält niemanden zurück.
 */
const wartendeKarte = new Map<string, HTMLElement>();

/**
 * Die Knöpfe EINES Ja einer Karte (meist einer; der Vorschlag mit Probestück hat zwei Wege: Probestück oder gleich das ganze
 * Lied). Gesperrt, sobald einer gedrückt ist — ein Druck sperrt ALLE, sonst ginge ein zweites Ja daneben; die Zeile darunter
 * sagt ehrlich, was geschieht: „Wird gesendet …“, dann die Zusage oder Absage des Servers (`meldeLiedStand`) — nie vorher
 * „Bestätigt“. Der erste Knopf ist der Hauptknopf.
 */
function jaKnoepfe(optionen: readonly JaOption[], abgelaufen: boolean, mitgesperrt: readonly HTMLInputElement[] = []): HTMLElement {
  const huelle = el("div", "lied-ja-huelle");
  const reihe = el("div", "tool-actions lied-ja");
  const knoepfe = optionen.map((o, i) => {
    const k = el("button", i === 0 ? "tool-action-btn primary" : "tool-action-btn", o.wort);
    k.type = "button";
    reihe.appendChild(k);
    return k;
  });
  const zeile = el("p", "lied-ja-stand");
  zeile.setAttribute("role", "status");
  const sperre = (satz: string): void => {
    for (const k of knoepfe) k.disabled = true;
    // Die Wahl auf der Karte gilt mit dem ersten Druck: danach lässt sie sich nicht mehr ändern.
    for (const e of mitgesperrt) e.disabled = true;
    zeile.textContent = satz;
  };
  /** Wieder frei: jeder Knopf, dessen Ja nicht schon gesendet ist (so bleibt ein abgeschlossener Weg zu). */
  const gibFrei = (): void => {
    optionen.forEach((o, i) => {
      knoepfe[i].disabled = schonJa(o.schluessel);
    });
    // Ein schon gesendetes Ja hält die Wahl fest; sonst ist sie wieder frei.
    for (const e of mitgesperrt) e.disabled = abgelaufen || optionen.some((o) => schonJa(o.schluessel));
  };
  let beantwortet = false;
  if (abgelaufen) sperre("Abgelaufen — für ein neues Ja den Vorschlag neu machen lassen.");
  else if (optionen.some((o) => schonJa(o.schluessel))) sperre("Gesendet.");
  // Die Antwort des Servers zu GENAU einem dieser Knöpfe (Vorschlag und Stufe) — live oder beim Neuladen aus dem Verlauf.
  const hoere = (ev: Event): void => {
    const d = (ev as CustomEvent<LiedStand>).detail;
    if (!knoepfe[0].isConnected && beantwortet) document.removeEventListener(LIED_STAND, hoere);
    const j = optionen.findIndex((o) => o.antwort === d?.schluessel);
    if (j < 0 || !d) return;
    // Auf den Druck einer ANDEREN Karte desselben Vorschlags wartet diese nicht.
    const wartet = wartendeKarte.get(d.schluessel);
    if (wartet && wartet !== huelle && wartet.isConnected) return;
    const o = optionen[j];
    beantwortet = true;
    sperre(d.satz);
    knoepfe.forEach((k, i) => k.classList.toggle("chosen", d.ok && i === j));
    if (d.ok) merkeJa(o.schluessel);
    // Eine Absage lässt den Vorschlag meist stehen („Der Vorschlag bleibt“): dann ist ein neues Ja möglich —
    // ein gesperrter Knopf neben „bitte bestätige ihn mit dem Knopf“ wäre ein Widerspruch. Abgelaufen bleibt aus.
    if (!d.ok && !abgelaufen) {
      vergissJa(o.schluessel);
      gibFrei();
    }
  };
  document.addEventListener(LIED_STAND, hoere);
  knoepfe.forEach((knopf, i) => {
    const o = optionen[i];
    /*
     * Nicht angekommen: die Knöpfe sind wieder frei, nichts ist gemerkt. (Vierte Prüfrunde: der Sendeweg des Chats
     * fängt jeden Fehler selbst ab — offline stand hier „Gesendet“, und nach dem Neuladen galt das Ja als gesendet.
     * Er meldet das Scheitern jetzt als `false`, frontend/app.ts `sendUserMessageFromCard`.)
     */
    const nichtGesendet = (): void => {
      if (beantwortet) return;
      zeile.textContent = navigator.onLine === false ? "Nicht gesendet — du bist offline." : "Nicht gesendet — bitte noch einmal tippen.";
      vergissJa(o.schluessel);
      gibFrei();
      knopf.classList.remove("chosen");
    };
    knopf.addEventListener("click", () => {
      if (knopf.disabled) return;
      // Je Druck neu: eine Absage des Servers auf den VORIGEN Druck sagt nichts über diesen.
      beantwortet = false;
      wartendeKarte.set(o.antwort, huelle);
      sperre("Wird gesendet …");
      knopf.classList.add("chosen");
      const senden = (
        window as unknown as { __voiceV8SendFromCard?: (t: string, bezug?: string) => Promise<boolean | void> }
      ).__voiceV8SendFromCard;
      if (!senden) return;
      senden(o.wort, typeof o.bezug === "function" ? o.bezug() : o.bezug).then((angekommen) => {
        if (angekommen === false) return nichtGesendet();
        // Kam keine Antwort zu diesem Knopf, steht sie im Gespräch darunter — nichts wird behauptet. Gemerkt wird
        // das Ja erst jetzt, da die Nachricht wirklich angekommen ist.
        if (beantwortet) return;
        zeile.textContent = "Gesendet — Vespera antwortet darunter.";
        merkeJa(o.schluessel);
      }, nichtGesendet);
    });
  });
  huelle.append(reihe, zeile);
  return huelle;
}

function fakten(teile: string[], beschriftung = "Bestellung"): HTMLElement {
  const liste = el("ul", "lied-fakten");
  liste.setAttribute("aria-label", beschriftung);
  for (const t of teile.filter(Boolean)) liste.appendChild(el("li", "", t));
  return liste;
}

function kopf(kicker: string, titel: string): HTMLElement[] {
  return [el("p", "lied-kicker", kicker), liedTitel(titel)];
}

/** Der Titel aus dem Grundgerüst, mit der Kennung der Liedkarte (Anzeigeschrift). */
function liedTitel(titel: string): HTMLElement {
  const t = kartenTitel(titel);
  t.classList.add("lied-titel");
  return t;
}

/** Die Wurzel jeder Liedkarte (Vorschlag, Probe, Lied) — aus dem Grundgerüst. */
function liedKarte(): HTMLDivElement {
  const karte = baueKarte("lied");
  karte.classList.add("tool-music-card");
  return karte;
}

/** Was die Prüfung fand — Abweichungen zuerst, sonst ein ruhiges „passt“. */
function pruefungBlock(r: Record<string, unknown>, mitDauer = true): HTMLElement | null {
  const abw = Array.isArray(r.abweichungen) ? r.abweichungen.filter((x): x is string => typeof x === "string") : [];
  const p = (r.pruefung ?? {}) as Pruefung;
  const box = el("div", abw.length ? "lied-pruefung lied-pruefung--abweichung" : "lied-pruefung");
  if (abw.length) {
    const kopfzeile = el("p", "lied-pruefung-kopf");
    kopfzeile.append(icon("alert", { size: 14 }), document.createTextNode(" Weicht von der Bestellung ab"));
    const liste = el("ul", "lied-pruefung-liste");
    for (const a of abw) liste.appendChild(el("li", "", a));
    box.append(kopfzeile, liste);
  } else if (p.passt === true) {
    const teile = ["Geprüft: passt zur Bestellung"];
    // Beim fertigen Lied steht die Dauer schon in der Plakette — nur einmal zeigen.
    if (mitDauer && typeof p.dauer_sek === "number" && Number.isFinite(p.dauer_sek)) teile.push(mmss(p.dauer_sek));
    const z = el("p", "lied-pruefung-kopf");
    z.append(icon("check", { size: 14 }), document.createTextNode(` ${teile.join(" · ")}`));
    box.appendChild(z);
  }
  if (p.ungeprueft) box.appendChild(el("p", "lied-pruefung-offen", p.ungeprueft));
  return box.childElementCount ? box : null;
}

/** Der Player — derselbe wie in Mediathek und Spiegel. Liefert seinen Startknopf, sobald er steht. */
function spieler(ziel: HTMLElement, url: string, titel: string): Promise<HTMLButtonElement | null> {
  return Promise.all([import("./audio-player.js"), import("./library-view.js")]).then(
    ([{ buildAudioPlayer }, { saubererDateiname }]) => {
      const p = buildAudioPlayer(url, { titel, sichern: { dateiname: saubererDateiname(titel, url) } });
      ziel.appendChild(p);
      return p.querySelector<HTMLButtonElement>(".player-bar-play");
    },
  );
}

const DIENST_ZUSATZ: Readonly<Record<LiedAnbieter, string>> = { lyria: "Standard", elevenlabs: "Studio-Qualität" };

/**
 * Die Wahl des Anbieters auf der Vorschlagskarte (Ticket 3071): zwei Felder in EINER Gruppe, jedes ein ganzes Tippziel. Zeigt nur, was der
 * Server zur Wahl stellt (`anbieter_wahl`: Tarif UND Schlüssel vorhanden). Liefert die Gruppe und den Leser der Wahl.
 */
function anbieterWahl(
  id: string,
  dienste: readonly LiedAnbieter[],
  vorgabe: LiedAnbieter,
  ueberschrift = "Anbieter",
): { gruppe: HTMLFieldSetElement; felder: HTMLInputElement[]; gewaehlt: () => LiedAnbieter } {
  const gruppe = el("fieldset", "lied-anbieter");
  gruppe.appendChild(el("legend", "lied-anbieter-name", ueberschrift));
  const name = `lied-anbieter-${id || "x"}-${++wahlZaehler}`;
  const reihe = el("div", "lied-anbieter-wahl");
  const felder: HTMLInputElement[] = [];
  for (const d of dienste) {
    const label = el("label", "lied-anbieter-option");
    const feld = el("input", "lied-anbieter-radio");
    feld.type = "radio";
    feld.name = name;
    feld.value = d;
    feld.checked = d === vorgabe;
    felder.push(feld);
    const text = el("span", "lied-anbieter-text");
    text.append(el("span", "lied-anbieter-dienst", ANBIETER_ANZEIGE[d]), el("span", "lied-anbieter-zusatz", DIENST_ZUSATZ[d]));
    label.append(feld, text);
    reihe.appendChild(label);
  }
  gruppe.appendChild(reihe);
  return { gruppe, felder, gewaehlt: () => dienste.find((_, i) => felder[i]?.checked) ?? vorgabe };
}

/** „Komponiert von Lyria“ — wer WIRKLICH komponiert hat, auch bei einem Ersatz (Ticket 3071). */
function dienstZeile(r: Record<string, unknown>, mitErsatz: boolean): HTMLElement | null {
  if (!istFortschrittAnbieter(r.anbieter)) return null;
  const teile = [`Komponiert von ${ANBIETER_ANZEIGE[r.anbieter]}`];
  if (mitErsatz && r.ersatz === true) teile.push("Ersatzdienst — der bestellte Dienst hat nicht geliefert");
  return fakten(teile, "Anbieter");
}

/**
 * Die Plakette im Kopf des fertigen Lieds: wer die GEHÖRTE Fassung komponiert hat und wie lang sie ist („Gehört: Lyria · 3:02“).
 * Sie steht allein im Kopf und ist nie ein Schalter — die Wahl für eine neue Fassung steht weiter unten, mit eigener Überschrift.
 */
function gehoertPlakette(r: Record<string, unknown>): HTMLElement | null {
  if (!istFortschrittAnbieter(r.anbieter)) return null;
  const dauer = ((r.pruefung ?? {}) as Pruefung).dauer_sek;
  const teile = [`Gehört: ${ANBIETER_ANZEIGE[r.anbieter]}`];
  if (typeof dauer === "number" && Number.isFinite(dauer)) teile.push(mmss(dauer));
  return el("p", "lied-gehoert", teile.join(" · "));
}

function baueVorschlag(r: Record<string, unknown>, jetzt: number): HTMLElement {
  const v = (r.vorschlag ?? {}) as Vorschlag;
  const id = text(r.vorschlag_id);
  const titel = text(v.title) || "Lied";
  const karte = liedKarte();
  // Ticket 2608: folgt im selben Zug das Lied, ersetzt es diese Karte (schritte.ts).
  karte.dataset.vorschlag = "ja";
  karte.dataset.stufe = "vorschlag";
  karte.append(...kopf("Liedvorschlag · noch nicht erzeugt", titel));
  // Zweite Prüfrunde: entstand der Vorschlag beim Lesen fremden Textes, sagt die Karte es zuerst.
  if (r.fremdtext === true) {
    const f = el("p", "lied-fremd", "Dieser Vorschlag entstand beim Lesen fremden Textes — prüfe ihn genau.");
    f.setAttribute("role", "note");
    karte.appendChild(f);
  }
  const gesang = v.gesang !== false && v.instrumental !== true;
  const laenge = typeof v.length_sec === "number" ? mmss(v.length_sec) : ""; // mmss liefert bei NaN/Infinity ""
  // Ticket 3071: der Anbieter steht auf der Karte; wo zwei zur Wahl stehen, ersetzt die Wahl die Marke „Standard/Studio“ (sie sagt dasselbe).
  const dienste = anbieterWahlAus(r.anbieter_wahl);
  const vorgabe: LiedAnbieter | null = istFortschrittAnbieter(r.anbieter) ? r.anbieter : null;
  const wahl = dienste.length ? anbieterWahl(id, dienste, vorgabe && dienste.includes(vorgabe) ? vorgabe : dienste[0]) : null;
  karte.appendChild(
    fakten([
      gesang ? `Gesang · ${text(v.sprache_name) || "?"}` : "Ohne Gesang",
      laenge,
      wahl ? "" : text(v.qualitaet),
      !wahl && vorgabe ? `Anbieter · ${ANBIETER_ANZEIGE[vorgabe]}` : "",
    ]),
  );
  if (wahl) karte.appendChild(wahl.gruppe);
  if (v.prompt) karte.appendChild(el("p", "lied-stil", v.prompt));
  const lyrics = text(v.lyrics);
  if (gesang && !lyrics) {
    // Ein Vorschlag aus der Zeit vor dem 07.10.2026 trug keinen Text.
    karte.appendChild(el("p", "lied-legende", "Auf diesem Vorschlag stand der Text noch nicht."));
  } else if (gesang) {
    const hinzu = Array.isArray(v.hinzugedichtet) ? v.hinzugedichtet.filter((x): x is string => typeof x === "string") : [];
    karte.appendChild(baueLiedBlatt(lyrics, hinzu, text(v.sprache) || undefined));
    if (hinzu.length) {
      const leg = el("p", "lied-legende");
      leg.append(el("mark", "lied-hinzu", "Markiert"), document.createTextNode(" = hinzugedichtet, nicht aus deinen Worten."));
      karte.appendChild(leg);
    } else {
      karte.appendChild(el("p", "lied-legende", "Alles aus deinen Worten — nichts hinzugedichtet."));
    }
  }
  // Mit Probestück: zwei Wege zum Ja. Ohne (der Dienst kann keines, oder „Musik ohne Probestück“ steht an): nur das Lied.
  const probe = r.naechster_schritt !== "lied";
  const ohneProbeEingestellt = r.ohne_probe === true;
  // Was das Ja kostet, steht auf der Karte — das Lied zählt ins Kontingent, das Probestück nicht (Ticket 3077).
  // Und ehrlich: das Probestück ist nur eine grobe Stilprobe, kein Ausschnitt des Lieds (Ticket 3075).
  const hinweis = probe
    ? [
        "Zuerst ein Probestück von 30 Sekunden — es zählt nicht.",
        "Es ist nur eine grobe Stilprobe: das fertige Lied kann anders klingen.",
        "Das ganze Lied direkt zählt ins Kontingent.",
      ]
    : [
        "Das ganze Lied entsteht nach deinem Ja gleich und zählt ins Kontingent.",
        ohneProbeEingestellt ? "Kein Probestück — so ist es unter „Musik ohne Probestück“ eingestellt." : "",
      ];
  karte.appendChild(el("p", "tool-music-hint", [text(r.stand), ...hinweis].filter(Boolean).join(" ")));
  const bis = Date.parse(text(r.gueltig_bis));
  // Die Wahl auf der Karte hängt HINTER die Kennung (der Server liest sie nur dort); ohne Wahl bleibt der Bezug, wie er war.
  const marke = (): string => (wahl ? anbieterMarke(wahl.gewaehlt()) : "");
  // Der Titel im Bezug ist gekürzt: app.ts schneidet den Bezug bei 200 Zeichen ab (sendUserMessageFromCard), und mit einem Titel von 120 Zeichen,
  // der Marke und der Liednummer fiele die Marke (oder die Nummer) hinten heraus. Die Kennung kommt immer vor der Marke und steht damit sicher.
  const kurzTitel = titel.length > 40 ? `${titel.slice(0, 39)}…` : titel;
  const bezug = (): string => `Liedvorschlag „${kurzTitel}“ · vorschlag_id ${id}${marke()}`;
  // Das Lied braucht seine Nummer (lied_nr), wie auf der Probestück-Karte.
  const bezugLied = (): string => (typeof r.lied_nr === "number" ? `${bezug()} · lied_nr ${r.lied_nr}` : bezug());
  const lied: JaOption = {
    wort: probe ? "Ja, ganzes Lied direkt erzeugen" : ohneProbeEingestellt ? "Ja, ganzes Lied erzeugen" : "Ja, Lied erzeugen",
    bezug: bezugLied,
    // Der direkte Weg ist ein eigener Knopf (`.direkt`), die Antwort des Servers aber die zum Lied (`.lied`).
    schluessel: `${id}.${probe ? "direkt" : "lied"}`,
    antwort: `${id}.lied`,
  };
  karte.appendChild(
    jaKnoepfe(
      probe ? [{ wort: "Ja, Probestück erzeugen", bezug, schluessel: `${id}.probe`, antwort: `${id}.probe` }, lied] : [lied],
      // Ohne Frist (ein Vorschlag von vor dem 07.10.) gilt er als abgelaufen.
      !id || !Number.isFinite(bis) || bis <= jetzt,
      wahl?.felder ?? [],
    ),
  );
  return karte;
}

function baueProbe(r: Record<string, unknown>): HTMLElement {
  const id = text(r.vorschlag_id);
  const titel = text(r.title).replace(/^Probestück:\s*/, "") || "Lied";
  const karte = liedKarte();
  karte.dataset.stufe = "probe";
  karte.append(...kopf("Probestück · 30 Sekunden · zählt nicht", titel));
  const dienst = dienstZeile(r, true);
  if (dienst) karte.appendChild(dienst);
  const ort = el("div", "lied-spieler");
  karte.appendChild(ort);
  const url = text(r.url);
  if (url) void spieler(ort, url, `Probestück: ${titel}`);
  const pr = pruefungBlock(r);
  if (pr) karte.appendChild(pr);
  // Ticket 3075: ein eigener Lauf, kein Anfang des Lieds — die Karte verspricht nicht, dass das Lied so klingt.
  karte.appendChild(el("p", "tool-music-hint", "Nur eine grobe Stilprobe aus einem eigenen Lauf — Stimme und Klang des fertigen Lieds können anders sein."));
  karte.appendChild(
    jaKnoepfe(
      [
        {
          wort: "Ja, ganzes Lied erzeugen",
          bezug: `Probestück „${titel}“ · vorschlag_id ${id} · lied_nr ${typeof r.lied_nr === "number" ? r.lied_nr : "?"}`,
          schluessel: `${id}.lied`,
          antwort: `${id}.lied`,
        },
      ],
      !id,
    ),
  );
  return karte;
}

/**
 * „Neu erzeugen“ unter dem fertigen Lied (Martin, 21.10.2026): der Dienst ist wählbar (ElevenLabs zuerst), der Knopf bleibt frei.
 * Er erzeugt NICHTS selbst — er bittet Vespera um einen neuen Liedvorschlag mit dem gewählten Dienst; das Ja (und die Kosten, die
 * der Vorschlag nennt) kommt wie immer auf der neuen Karte. So bleibt das Ja-Tor je Erzeugung.
 */
function neuErzeugenBlock(r: Record<string, unknown>, titel: string): HTMLElement | null {
  const dienste = anbieterWahlAus(r.anbieter_wahl);
  if (!dienste.length) return null;
  const id = text(r.vorschlag_id);
  // Vorauswahl = der ANDERE Anbieter als der der gehörten Fassung (sonst käme dieselbe Fassung noch einmal).
  const gehoert = istFortschrittAnbieter(r.anbieter) ? r.anbieter : null;
  const wahl = anbieterWahl(id, dienste, dienste.find((d) => d !== gehoert) ?? dienste[0], "Anbieter für die neue Fassung");
  const huelle = el("section", "lied-neu");
  const ueberschrift = el("p", "lied-neu-titel", "Noch eine Fassung");
  ueberschrift.id = `lied-neu-${++wahlZaehler}`;
  huelle.setAttribute("aria-labelledby", ueberschrift.id);
  const knopf = el("button", "tool-action-btn", "Neu erzeugen");
  knopf.type = "button";
  const zeile = el("p", "lied-ja-stand");
  zeile.setAttribute("role", "status");
  knopf.addEventListener("click", () => {
    if (knopf.disabled) return;
    const dienst = wahl.gewaehlt();
    const senden = (window as unknown as { __voiceV8SendFromCard?: (t: string, bezug?: string) => Promise<boolean | void> }).__voiceV8SendFromCard;
    if (!senden) return;
    knopf.disabled = true;
    zeile.textContent = "Wird gesendet …";
    // Der Titel kommt aus einem Modell-Ergebnis: ohne Steuerzeichen, Anführungszeichen und Umbrüche, auf 40 Zeichen gekürzt.
    const sauber = titel.replace(/[\p{Cc}\p{Cf}\u2028\u2029"'„“”‚‘’«»‹›`]+/gu, " ").replace(/\s+/g, " ").trim() || "Lied";
    const kurz = sauber.length > 40 ? `${sauber.slice(0, 39)}…` : sauber;
    // Nur die Kennung des alten Vorschlags (kein Text), damit Vespera Text und Stil sicher findet — ohne das Wort des Knopfs.
    const alt = /^[0-9a-f]{12}$/.test(id) ? ` · Vorlage ${id}` : "";
    senden(
      `Mach „${kurz}“ noch einmal mit ${ANBIETER_ANZEIGE[dienst]} — neuer Liedvorschlag (anbieter ${dienst}), gleicher Text und Stil.`,
      `Lied „${kurz}“ · neu erzeugen · anbieter ${dienst}${alt}`,
    ).then(
      (ok) => {
        zeile.textContent = ok === false ? "Nicht gesendet — bitte noch einmal tippen." : "Gesendet — der neue Vorschlag kommt darunter, das Ja gibst du dort.";
        knopf.disabled = false;
      },
      () => {
        zeile.textContent = "Nicht gesendet — bitte noch einmal tippen.";
        knopf.disabled = false;
      },
    );
  });
  huelle.append(ueberschrift, wahl.gruppe, knopf, zeile);
  return huelle;
}

function baueLied(r: Record<string, unknown>): HTMLElement {
  const titel = text(r.title) || "Musik";
  const karte = liedKarte();
  karte.dataset.stufe = "lied";
  karte.appendChild(liedTitel(titel));
  const url = text(r.url);
  if (!url) return karte;
  const plakette = gehoertPlakette(r);
  if (plakette) karte.appendChild(plakette);
  const ort = el("div", "lied-spieler");
  karte.appendChild(ort);
  void spieler(ort, url, text(r.title).trim() || "Lied");
  const pr = pruefungBlock(r, !plakette);
  if (pr) karte.appendChild(pr);
  // Der gesungene Text, eingeklappt — Lyria liefert ihn mit.
  const lt = text(r.liedtext);
  if (lt) {
    const d = el("details", "tool-music-text");
    const sum = el("summary", "", "Liedtext");
    const body = el("p", "tool-music-text-body", lt);
    d.append(sum, body);
    karte.appendChild(d);
  }
  const neu = neuErzeugenBlock(r, titel);
  if (neu) karte.appendChild(neu);
  // Ein Ersatz heißt so — nie „Studio“, wenn keiner es bestellt hat.
  const q = text(r.qualitaet);
  const qualitaet = q === "Studio" ? "Studio-Qualität." : q.includes("Ersatz") ? "Über den Ersatzdienst erzeugt." : "";
  const gezaehlt = r.gezaehlt === false ? "Zählt nicht ins Kontingent." : "";
  karte.appendChild(
    el("p", "tool-music-hint", ["Gespeichert in der Mediathek.", qualitaet, gezaehlt, text(r.stand)].filter(Boolean).join(" ")),
  );
  return karte;
}

/** Die Karte zu einem Ergebnis von generate_music. */
export function baueLiedKarte(result: unknown, jetzt: number = Date.now()): HTMLElement {
  const r = auspacken(result);
  if (r.status === "vorschlag") return baueVorschlag(r, jetzt);
  if (r.stufe === "probe" && r.erzeugt === true) return baueProbe(r);
  return baueLied(r);
}

/**
 * „Spiel das Lied noch einmal“ (Werkzeug abspielen, ziel lied): der Player
 * des Stücks aus der Mediathek. Gestartet wird über SEINEN Knopf, erst nach
 * Vesperas Satz (frontend/lib/abspielen.ts) — nie beim Bau, sonst spielte
 * eine Karte aus dem Verlauf los.
 */
export function baueLiedAbspielKarte(result: unknown, onAbspieler?: (a: Abspieler) => void): HTMLElement {
  const r = auspacken(result);
  const titel = text(r.titel) || "Lied";
  const karte = baueKarte("abspielen");
  karte.classList.add("tool-lied");
  karte.appendChild(liedTitel(titel));
  const ort = el("div", "lied-spieler");
  karte.appendChild(ort);
  const knopf = spieler(ort, text(r.url), titel);
  onAbspieler?.({
    spielen: () =>
      void knopf.then((k) => {
        // Nur eine Karte, die im Chat steht, und nur, wenn sie nicht schon läuft.
        if (k?.isConnected && k.dataset.state !== "laeuft") k.click();
      }),
    anbieten: () =>
      void knopf.then((k) => {
        if (!k?.isConnected) return;
        ort.before(baueTippHinweis());
        k.focus();
      }),
  });
  return karte;
}
